import { useMemo, useState } from "react";
import { useRouteContext } from "@tanstack/react-router";
import {
  type GridRowClickEvent,
  type GridColumnProps,
} from "@progress/kendo-react-grid";

import { type PRGM } from "@/models";
import { CHLD } from "@/models/chld";
import KendoTableClientState from "@/components/shared/tables/KendoTableClientState";
import TooltipTrigger from "@/components/shared/TooltipTrigger";
import { FiPlus } from "react-icons/fi";
import { formatDateOnly } from "@/utils/dates";
import {
  createNamedFilename,
  CSVDownloadButton,
} from "@/components/shared/CSVDownloadButton";
import { useCoreData } from "@/queries.ts";
import PRGMEditDialog from "./dialogs/PRGMEditDialog";

const columns: GridColumnProps[] = [
  { id: "ProgramYear", field: "ProgramYear", title: "Program Year" },
  { id: "PreschoolName", field: "PreschoolName", title: "School" },
  { id: "LocalChildId", field: "LocalChildId", title: "Local Child ID (CICN)" },
  { id: "CSPPID", field: "CSPPID", title: "CSPPID" },
  { id: "EduProgramName", field: "EduProgramName", title: "Education Program" },
  {
    id: "EduProgramMembershipStartDate",
    field: "EduProgramMembershipStartDate",
    title: "Program Start Date",
  },
];

const csvHeaders = [
  { label: "Program Year", key: "ProgramYear" },
  { label: "School", key: "PreschoolName" },
  { label: "Local Child ID (CICN)", key: "LocalChildId" },
  { label: "CSPPID", key: "CSPPID" },
  { label: "Education Program", key: "EduProgramName" },
  { label: "Program Start Date", key: "EduProgramMembershipStartDate" },
];

type PrgmRow = PRGM & {
  PreschoolName?: string;
  EduProgramName?: string;
  ChildName?: string;
  __raw: PRGM;
};

export default function PRGMRecordsTab({
  prgmRecords,
  child,
}: {
  prgmRecords: PRGM[];
  child: CHLD;
}) {
  const leaContext = useRouteContext({ from: "/$leaid" });
  const coreData = useCoreData();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [popupPrgm, setPopupPrgm] = useState<PRGM | undefined>(undefined);
  const [prgmDefaults, setPrgmDefaults] = useState<Partial<PRGM> | undefined>(
    undefined
  );

  const canEdit = leaContext.hasRole("PRGMEdit");

  const closeDialog = () => {
    setDialogOpen(false);
    setPopupPrgm(undefined);
    setPrgmDefaults(undefined);
  };

  const onAddButtonClick = () => {
    setPrgmDefaults({
      ReportingLEA: child.ReportingLEA,
      CSPPID: child.CSPPID,
      LocalChildId: child.LocalChildId,
      ProgramYear: child.ProgramYear,
      PreschoolCode: child.PreschoolCode,
    });
    setPopupPrgm(undefined);
    setDialogOpen(true);
  };

  const onEditButtonClick = (record: PRGM) => {
    setPrgmDefaults(undefined);
    setPopupPrgm(record);
    setDialogOpen(true);
  };

  const childName = [
    child.ChildFirstName,
    child.ChildMiddleName,
    child.ChildLastName,
  ]
    .filter(Boolean)
    .join(" ");

  const tableData: PrgmRow[] = useMemo(() => {
    return (prgmRecords ?? []).map((record) => ({
      ...record,
      ChildName: childName,
      PreschoolName: leaContext?.currentOrgPreschools?.find(
        (x) => x.PreschoolCDSCode === record.PreschoolCode
      )?.PreschoolName,
      EduProgramName:
        coreData.educationalProgramList.find(
          (x) => x.Value === record.EduProgramCode
        )?.Text ?? record.EduProgramCode,
      EduProgramMembershipStartDate: record.EduProgramMembershipStartDate
        ? formatDateOnly(record.EduProgramMembershipStartDate)
        : "",
      __raw: record,
    }));
  }, [
    prgmRecords,
    leaContext?.currentOrgPreschools,
    coreData.educationalProgramList,
    childName,
  ]);

  return (
    <>
      <div className="d-flex justify-content-end align-items-center mb-3">
        <TooltipTrigger tooltipText="Export education program records">
          <CSVDownloadButton
            data={tableData}
            headers={csvHeaders}
            filename={createNamedFilename(["ChildName"], "education-program-record")}
          />
        </TooltipTrigger>

        {canEdit && (
          <TooltipTrigger tooltipText="Add an education program record">
            <button
              type="button"
              className="btn btn-primary"
              onClick={onAddButtonClick}
            >
              <FiPlus className="me-1" />
              Add
            </button>
          </TooltipTrigger>
        )}
      </div>

      <KendoTableClientState
        data={tableData}
        columns={columns}
        onRowClick={(event: GridRowClickEvent) =>
          onEditButtonClick((event.dataItem as PrgmRow).__raw)
        }
      />

      {dialogOpen && (
        <PRGMEditDialog
          selectedPrgm={popupPrgm}
          prgmDefaults={prgmDefaults}
          onClose={closeDialog}
        />
      )}
    </>
  );
}
